import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchAuthSession } from 'aws-amplify/auth';

import Layout from '@/features/reservation/components/Layout';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationList } from './NotificationList';
import { FacilityResponseType } from '@/features/reservation/types/FacilityResponseType';
import { executeGet } from '@/client/rest';

export const InAppNotification = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const [patientId, setPatientId] = useState<string | null>(null);
  const [clinicName, setClinicName] = useState<string>('病院名');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    hasMore,
    isLoading,
    isInitialLoading, // 初回ローディング状態を追加
    loadMoreNotifications
  } = useNotifications(patientId ?? '', facilityId ?? '');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatientId = async () => {
      try {
        const session = await fetchAuthSession();
        setPatientId(session.userSub ?? null);
      } catch (error) {
        console.error('Failed to fetch auth session:', error);
      }
    };
    fetchPatientId();

    const handleSetFacilityName = async () => {
      const facilityList: FacilityResponseType = await executeGet<FacilityResponseType>({
        apiName: 'medirecoWebApi',
        path: '/get-facility-list'
      });
      const facility = facilityList.facilities?.find((facility) => facility.id === facilityId);
      if (!facility) {
        // 施設一覧が取得できない場合は施設一覧に遷移
        const redirectUrl = '/patient/notifications/facilities';
        const encodedRedirectUrl = encodeURIComponent(redirectUrl);
        navigate(`/patient/facilities?redirect=${encodedRedirectUrl}`, { replace: true });
        return;
      }
      const facilityNameResponse = facility.facility_name;
      if (facilityNameResponse === undefined) {
        // クエリパラメータにある施設IDが不適切なもの(例:0000)である場合、NotFoundページへ遷移
        navigate('/notFound');
        return;
      }
      setClinicName(facilityNameResponse);
    };

    // クエリパラメータにある施設IDから施設名を取得してセットする
    handleSetFacilityName();
  }, []);

  const handleScroll = useCallback(async () => {
    if (!scrollRef.current || isLoading || !hasMore) return;

    const scrollElement = scrollRef.current;
    if (scrollElement.scrollTop === 0) {
      // 最上部に到達した場合にデータをロード
      setLoading(true);

      const previousScrollHeight = scrollElement.scrollHeight;

      await loadMoreNotifications();

      // スクロール位置を補正
      requestAnimationFrame(() => {
        if (!scrollRef.current) return;

        const newScrollHeight = scrollElement.scrollHeight;
        scrollElement.scrollTop = newScrollHeight - previousScrollHeight;
      });
      setLoading(false);
    }
  }, [isLoading, hasMore, loadMoreNotifications]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      requestAnimationFrame(() => {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      });
    }
  }, []);

  return (
    <Layout title={clinicName} backButton handleBack={() => navigate(-1)}>
      <Box
        ref={scrollRef}
        sx={{
          height: '80vh',
          overflow: 'auto',
          mt: 3
        }}
        onScroll={handleScroll}
      >
        {isInitialLoading ? (
          // 初回ローディング中のスピナー表示
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <NotificationList notifications={notifications} isLoading={isLoading} />
        )}
      </Box>
    </Layout>
  );
};

export default InAppNotification;
